import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root',
})
export class BookingCounterService {
  private pendingCountSubject = new BehaviorSubject<number>(0);
  public pendingCount$ = this.pendingCountSubject.asObservable();

  private unsubscribe: (() => void) | null = null;

  constructor(
    private firestore: Firestore,
    private auth: Auth,
  ) {
    this.init();
  }

  private init() {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        // ── WORKER SUPPORT: use parent vendor's UID for booking queries ──
        let effectiveUid = user.uid;
        try {
          const userSnap = await getDoc(doc(this.firestore, 'users-list', user.uid));
          if (userSnap.exists()) {
            const data = userSnap.data() as any;
            if (data.role === 'worker' && data.parentUid) {
              effectiveUid = data.parentUid;
            }
          }
        } catch (_) { /* fall back to own uid */ }
        // ── END WORKER SUPPORT ─────────────────────────────────────────────
        this.subscribeToPendingBookings(effectiveUid);
      } else {
        this.cleanup();
      }
    });
  }

  private subscribeToPendingBookings(vendorId: string) {
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const bookingsRef = collection(this.firestore, 'booking-list');

    // Query for bookings where:
    // 1. vendorId matches the effective vendor UID
    // 2. status is 'Pending'
    const q = query(
      bookingsRef,
      where('vendorId', '==', vendorId),
      where('status', '==', 'Pending'),
    );

    this.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        this.pendingCountSubject.next(snapshot.size);
      },
      (error) => {
        console.error('Error fetching pending bookings count: ', error);
      },
    );
  }

  private cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.pendingCountSubject.next(0);
  }
}
